import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useFleetCosts, useFleetCostsSummary, CostData, VehicleCost } from "@/hooks/use-fleet-costs";
import { useTrendAnalysis } from "@/hooks/use-trend-analysis";
import { TrendChart } from "@/components/fleet/reports/TrendChart";
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Fuel, 
  Wrench, 
  FileText, 
  Calendar,
  BarChart3,
  PieChart
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Area, 
  AreaChart, 
  ResponsiveContainer, 
  Tooltip, 
  XAxis, 
  YAxis, 
  CartesianGrid 
} from "recharts";

// Using interfaces from hook

export default function FleetCosts() {
  const { profile } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState("month");
  const [selectedYear, setSelectedYear] = useState("2025");

  // Get current user's company
  const { data: userCompanies } = useQuery({
    queryKey: ["userCompanies"],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_companies")
        .select("company_id, companies(name)")
        .eq("user_id", profile?.id);
      return data || [];
    },
    enabled: !!profile?.id
  });

  const currentCompanyId = userCompanies?.[0]?.company_id;

  // Get real costs data
  const { data: costsData, isLoading } = useFleetCosts(currentCompanyId, selectedPeriod, selectedYear);
  const { data: summaryData } = useFleetCostsSummary(currentCompanyId);
  const { data: trendData } = useTrendAnalysis(currentCompanyId || '', 'cost', 6);

  const costCategories = costsData?.categories || [];
  const vehicleCosts = costsData?.vehicles || [];

  const totalCosts = summaryData?.totalCost || 0;
  const monthlyAverage = summaryData?.monthlyAverage || 0;
  const topCategory = costCategories[0];
  const costTrendDirection = (summaryData?.trend || 0) > 0 ? "up" : "down";

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Custos da Frota</h1>
          <p className="text-muted-foreground">
            Análise financeira e controle de custos operacionais
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Mensal</SelectItem>
              <SelectItem value="quarter">Trimestral</SelectItem>
              <SelectItem value="year">Anual</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2023">2023</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Custo Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalCosts)}</div>
            <p className="text-xs text-muted-foreground">
              período selecionado
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Média Mensal</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(monthlyAverage)}</div>
            <p className="text-xs text-muted-foreground">
              custo médio mensal
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Maior Categoria</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(topCategory?.amount || 0)}</div>
            <CardDescription>{topCategory?.category || 'N/A'}</CardDescription>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tendência</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <span className={`text-sm ${costTrendDirection === "up" ? "text-green-600" : "text-red-600"} flex items-center gap-1`}>
                {costTrendDirection === "up" ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                {formatPercentage(summaryData?.trend || 0)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              vs período anterior
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analysis */}
      <Tabs defaultValue="categories" className="space-y-4">
        <TabsList>
          <TabsTrigger value="categories">Por Categoria</TabsTrigger>
          <TabsTrigger value="vehicles">Por Veículo</TabsTrigger>
          <TabsTrigger value="trends">Tendências</TabsTrigger>
          <TabsTrigger value="reports">Relatórios</TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Custos por Categoria</CardTitle>
                <CardDescription>
                  Distribuição de custos por tipo de despesa
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {isLoading ? (
                  <div>Carregando...</div>
                ) : costCategories.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    Nenhum dado de custo encontrado para o período selecionado
                  </div>
                ) : (
                  costCategories.map((category) => (
                    <div key={category.category} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {category.category === "Combustível" && <Fuel className="h-5 w-5 text-blue-500" />}
                        {category.category === "Manutenção" && <Wrench className="h-5 w-5 text-green-500" />}
                        {category.category === "Documentação" && <FileText className="h-5 w-5 text-purple-500" />}
                        <div>
                          <p className="font-medium">{category.category}</p>
                          <p className="text-sm text-muted-foreground">{category.count} registros</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatCurrency(category.amount)}</p>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{category.percentage.toFixed(1)}%</Badge>
                          <span className={`text-xs ${category.change >= 0 ? "text-green-600" : "text-red-600"}`}>
                            {formatPercentage(category.change)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Evolução Mensal</CardTitle>
                <CardDescription>
                  Gráfico de evolução dos custos ao longo do tempo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={trendData?.historical || []}>
                    <defs>
                      <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="period" 
                      className="text-xs"
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis 
                      className="text-xs"
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-background border rounded-lg p-3 shadow-lg">
                              <p className="text-sm font-medium">{payload[0].payload.period}</p>
                              <p className="text-sm text-primary font-semibold">
                                {formatCurrency(payload[0].value as number)}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="hsl(var(--primary))" 
                      fillOpacity={1} 
                      fill="url(#colorCost)" 
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="vehicles" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Custos por Veículo</CardTitle>
              <CardDescription>
                Detalhamento de custos individuais da frota
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {isLoading ? (
                  <div>Carregando...</div>
                ) : vehicleCosts.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    Nenhum dado de custo encontrado para o período selecionado
                  </div>
                ) : (
                  vehicleCosts.map((vehicle) => (
                    <div key={vehicle.vehicle_id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold">{vehicle.plate}</h4>
                          <p className="text-sm text-muted-foreground">
                            {vehicle.make} {vehicle.model}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">{formatCurrency(vehicle.total_cost)}</p>
                          <p className="text-sm text-muted-foreground">Total</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Combustível</p>
                          <p className="font-medium">{formatCurrency(vehicle.fuel_cost)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Manutenção</p>
                          <p className="font-medium">{formatCurrency(vehicle.maintenance_cost)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Seguro</p>
                          <p className="font-medium">{formatCurrency(vehicle.insurance_cost)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Outros</p>
                          <p className="font-medium">{formatCurrency(vehicle.other_cost)}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
          {trendData ? (
            <TrendChart
              data={trendData}
              metric="Custos Totais"
              unit="R$"
              color="hsl(var(--primary))"
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Análise de Tendências</CardTitle>
                <CardDescription>
                  Projeções e análises preditivas de custos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px] flex items-center justify-center">
                  <p className="text-muted-foreground">Carregando dados de tendência...</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Relatórios Financeiros</CardTitle>
              <CardDescription>
                Gere relatórios detalhados dos custos da frota
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <Button variant="outline" className="h-24 flex-col gap-2">
                  <FileText className="h-6 w-6" />
                  <span>Relatório Mensal</span>
                </Button>
                <Button variant="outline" className="h-24 flex-col gap-2">
                  <BarChart3 className="h-6 w-6" />
                  <span>Análise Comparativa</span>
                </Button>
                <Button variant="outline" className="h-24 flex-col gap-2">
                  <DollarSign className="h-6 w-6" />
                  <span>Projeção de Custos</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}